import { inject, Injectable, signal } from '@angular/core';
import { LabelsRepo } from '@client/app/feature-modules/admin/modules/admin-labels/services/labels.repo';
import { Label } from '@client/app/feature-modules/admin/modules/admin-labels/types/label.type';

@Injectable()
export class LabelDataService {
    labels = signal<Label[]>(
        [],
        {
            equal: (a, b) => {
                if (a.length !== b.length) {
                    return false;
                }
                for (let i = 0; i < a.length; i++) {
                    if (a[i].id !== b[i].id) {
                        return false;
                    }
                }
                return true;
            },
        },
    );
    fetchingLabels = signal(false);
    page = signal(1);
    search = signal('');
    pageSize = signal(20);
    total = signal(0);
    allLabelsFetched = signal(false);

    private readonly labelsRepo = inject(LabelsRepo);

    async setSearchQuery (query: string) {
        if (query === this.search()) {
            return;
        }
        this.reset();
        this.search.set(query);
        await this.fetch({ page: 1, pageSize: this.pageSize() });
    }

    async createAndAppendLabel (params: { name: string, color: string }) {
        const label = await this.labelsRepo.createLabel({
            name: params.name,
            color: params.color,
        });
        this.labels.set(this.labels().concat([label]));
        this.total.update(val => val + 1);
        return label;
    }

    async fetch (payload: { page: number, pageSize: number }) {
        if (this.allLabelsFetched()) {
            return;
        }
        this.page.set(payload.page);
        this.pageSize.set(payload.pageSize);
        this.fetchingLabels.set(true);
        try {
            const results = await this.labelsRepo.getLabels({
                search: this.search(),
                page: this.page(),
                page_size: this.pageSize(),
            });

            this.total.set(results?.total || 0);
            this.allLabelsFetched.set(this.total() < this.page() * this.pageSize());
            this.labels.set(this.labels().concat(results));
        } finally {
            this.fetchingLabels.set(false);
        }
    }

    reset (): void {
        this.labels.set([]);
        this.page.set(1);
        this.total.set(0);
        this.search.set('');
        this.allLabelsFetched.set(false);
    }
}
